import React, { useCallback, useEffect, useRef, useState } from "react";
import DangerInfo from "../DangerInfo";
import InfoPopup from "../InfoPopup";
import { calculateLocationRating } from "../InfoPopup";
import FeatureConsentModal from "../FeatureConsentModal";
import { useTranslation } from "react-i18next";
import "../../styles/components/MapLegend.css";
import API_BASE, { withApiCredentials } from "../../api";
import { getActiveAwareAuthToken } from "../../auth/bootstrapTokens";
import AppBackButton from "../common/AppBackButton";
import {
  Camera,
  ChevronRight,
  Fish,
  Heart,
  Images,
  Info,
  MapPin,
  Navigation,
  Search,
  Share2,
  Star,
  TriangleAlert,
  X,
} from "lucide-react";


function renderStars(value) {
  const full = Math.floor(value);
  const half = value % 1 >= 0.5 ? 1 : 0;
  const empty = 5 - full - half;
  return "\u2605".repeat(full) + (half ? "\u2606" : "") + "\u2606".repeat(empty);
}

/* ---------------------- MapLegend Component ---------------------- */
export default function MapLegend({
  selectedSpot,
  posts,
  onClose,
  onOpenProfile,
  onAddFavoriteSpot,
  viewer,
  onUserUpdated,
  showReviews = false,
  onShowReviewsChange,
  selectedPostInfo = null,
  onSelectedPostInfoChange,
  showDanger = false,
  onShowDangerChange,
  expandedImage = null,
  onExpandedImageChange,
  expandedSource = null,
  onExpandedSourceChange,
  showShareModal = false,
  onShowShareModalChange,
  showLocationConsent = false,
  onShowLocationConsentChange,
  onPostInfoNavigateAway,
}) {
  const { t } = useTranslation();
  const renderLabel = (val) => {
  if (!val) return "";
  return typeof val === "string" && val.startsWith("map_")
    ? t(val)
    : val;
};

  const roundFavoriteCoordinate = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    return Number(number.toFixed(6));
  };

  const isSameFavoriteSpot = (left, right) => {
    const leftLat = roundFavoriteCoordinate(left?.lat);
    const leftLng = roundFavoriteCoordinate(left?.lng);
    const rightLat = roundFavoriteCoordinate(right?.lat);
    const rightLng = roundFavoriteCoordinate(right?.lng);

    return (
      leftLat !== null &&
      leftLng !== null &&
      leftLat === rightLat &&
      leftLng === rightLng
    );
  };

  const buildFavoriteSpotPayload = () => ({
    name:
      derivedPlaceName ||
      renderLabel(selectedSpot?.name) ||
      renderLabel(selectedSpot?.location) ||
      `${selectedSpot?.lat?.toFixed?.(3)}, ${selectedSpot?.lng?.toFixed?.(3)}`,
    location:
      renderLabel(selectedSpot?.location) ||
      derivedPlaceName ||
      renderLabel(selectedSpot?.name) ||
      "",
    lat: selectedSpot?.lat,
    lng: selectedSpot?.lng,
  });

  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState(false);
const [spotImages, setSpotImages] = useState([]);
const [uploading, setUploading] = useState(false);
const [activeImageIndex, setActiveImageIndex] = useState(0);
const [derivedPlaceName, setDerivedPlaceName] = useState(null);
const [friends, setFriends] = useState([]);
const [friendQuery, setFriendQuery] = useState("");
const [friendsLoading, setFriendsLoading] = useState(false);
const [friendsError, setFriendsError] = useState(false);
const [sharing, setSharing] = useState(false);
const [pendingShareFriendId, setPendingShareFriendId] = useState(null);
const [isSavingLocationConsent, setIsSavingLocationConsent] = useState(false);
const [locationConsentError, setLocationConsentError] = useState("");
  const contentRef = useRef(null);
  const noop = useCallback(() => {}, []);
  const setShowReviews = onShowReviewsChange || noop;
  const setSelectedPostInfo = onSelectedPostInfoChange || noop;
  const setShowDanger = onShowDangerChange || noop;
  const setExpandedImage = onExpandedImageChange || noop;
  const setExpandedSource = onExpandedSourceChange || noop;
  const setShowShareModal = onShowShareModalChange || noop;
  const setShowLocationConsent = onShowLocationConsentChange || noop;



// Reset to main view when a new spot is selected
useEffect(() => {
  setShowReviews(false);
  setShowDanger(false);
  setSelectedPostInfo(null);
  setExpandedImage(null);
  setExpandedSource(null);
  setShowShareModal(false);
  setFriendQuery("");
  setShowLocationConsent(false);
  setPendingShareFriendId(null);
  setLocationConsentError("");
  setIsUpdatingFavorite(false);
}, [
  selectedSpot,
  setExpandedImage,
  setExpandedSource,
  setSelectedPostInfo,
  setShowLocationConsent,
  setShowDanger,
  setShowReviews,
  setShowShareModal,
]);

useEffect(() => {
  async function loadFriends() {
    if (!showShareModal) return;

    setFriendsLoading(true);
    setFriendsError(false);
    try {
      const token = getActiveAwareAuthToken(window);
      if (!token && !viewer?.id) {
        setFriends([]);
        return;
      }

      const res = await fetch(
        `${API_BASE}/auth/me`,
        withApiCredentials({
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
      );
      if (!res.ok) throw new Error("Failed to load current user");

      const me = await res.json();
      const friendIds = me.friends || viewer?.friends || [];

      if (friendIds.length === 0) {
        setFriends([]);
        return;
      }

      // fetch all users so we can map ids to users
      const ures = await fetch(`${API_BASE}/auth/users`);
      if (!ures.ok) throw new Error("Failed to load users");

      const users = await ures.json();
      const map = {};
      users.forEach((u) => (map[u.id] = u));

      const resolved = friendIds
        .map((id) => map[id])
        .filter(Boolean);

      setFriends(resolved);
    } catch (e) {
      console.error("Failed loading friends", e);
      setFriendsError(true);
    } finally {
      setFriendsLoading(false);
    }
  }

  loadFriends();
}, [showShareModal, viewer?.friends, viewer?.id]);


// Derive place name from coordinates
useEffect(() => {
  async function fetchPlaceName() {
    if (!selectedSpot?.lat || !selectedSpot?.lng) {
      setDerivedPlaceName(null);
      return;
    }

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${selectedSpot.lat}&lon=${selectedSpot.lng}`
      );
      const data = await res.json();

      const name =
        data?.address?.city ||
        data?.address?.town ||
        data?.address?.village ||
        data?.address?.hamlet ||
        data?.address?.county ||
        data?.display_name;

      setDerivedPlaceName(name || null);
    } catch (e) {
      console.warn("Reverse geocoding failed", e);
      setDerivedPlaceName(null);
    }
  }

  fetchPlaceName();
}, [selectedSpot]);

  useEffect(() => {
  async function loadImages() {
    try {
      const token = getActiveAwareAuthToken(window);
      if (
  !selectedSpot?.id ||
  !selectedSpot.id.includes("_") ||
  selectedSpot.isPrivate === true
) {
  setSpotImages([]);
setActiveImageIndex(0);
return;

}
      if (!token && !viewer?.id) {
        setSpotImages([]);
        setActiveImageIndex(0);
        return;
      }


      const res = await fetch(
        `${API_BASE}/map/spots/${selectedSpot.id}/images`
,
        withApiCredentials({
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
      );

      if (!res.ok) {
        setSpotImages([]);
setActiveImageIndex(0);
return;

      }

      const data = await res.json();
      setSpotImages(Array.isArray(data) ? data : []);
      setActiveImageIndex(0);

    } catch {
  setSpotImages([]);
  setActiveImageIndex(0);
}

  }

  loadImages();
}, [selectedSpot, viewer?.id]);


if (!selectedSpot) return null;

  const legendTitle = derivedPlaceName
    ? derivedPlaceName
    : selectedSpot.location && selectedSpot.location !== "\u2014"
      ? renderLabel(selectedSpot.location)
      : selectedSpot.name
        ? renderLabel(selectedSpot.name)
        : `${selectedSpot.lat.toFixed(3)}, ${selectedSpot.lng.toFixed(3)}`;
  const favoriteSpots = Array.isArray(viewer?.favoriteSpots)
    ? viewer.favoriteSpots
    : [];
  const isFavorited = favoriteSpots.some((spot) =>
    isSameFavoriteSpot(spot, selectedSpot)
  );



  // Filter posts that belong to this spot
  const spotReviews = posts.filter((p) => {
    const d = p.catch || p;
    if (!d.coordinates) return false;

    const nums = d.coordinates.match(/(-?\d+(\.\d+)?)/g);
    if (!nums || nums.length < 2) return false;

    const lt = parseFloat(nums[0]);
    const lg = parseFloat(nums[1]);

    return (
      Math.abs(lt - selectedSpot.lat) < 0.002 &&
      Math.abs(lg - selectedSpot.lng) < 0.002
    );
  });
  const locationRatingStats = calculateLocationRating(
    posts,
    `${selectedSpot.lat},${selectedSpot.lng}`
  );
  const catchCount = spotReviews.length;

  const handleFavoriteClick = async () => {
    if (!onAddFavoriteSpot || isUpdatingFavorite) {
      return;
    }

    setIsUpdatingFavorite(true);
    try {
      await onAddFavoriteSpot(buildFavoriteSpotPayload());
    } finally {
      setIsUpdatingFavorite(false);
    }
  };

  const resetContentScroll = () => {
    const content = contentRef.current;
    if (!content) return;

    if (typeof content.scrollTo === "function") {
      content.scrollTo({ top: 0, behavior: "auto" });
    } else {
      content.scrollTop = 0;
    }
  };

  const closeShareSubview = () => {
    setShowShareModal(false);
    setFriendQuery("");
    setSharing(false);
    setPendingShareFriendId(null);
    setLocationConsentError("");
    setShowLocationConsent(false);
  };

  const showSummary = () => {
    setShowReviews(false);
    setShowDanger(false);
    closeShareSubview();
    resetContentScroll();
  };

  const handleOpenLocationCatches = () => {
    if (catchCount > 0) {
      setShowDanger(false);
      closeShareSubview();
      setShowReviews(true);
      resetContentScroll();
    }
  };

  const handleOpenDangers = () => {
    setShowReviews(false);
    closeShareSubview();
    setShowDanger(true);
    resetContentScroll();
  };

  const handleOpenShare = () => {
    setShowDanger(false);
    setShowReviews(false);
    setShowShareModal(true);
    resetContentScroll();
  };

  const handleClosePanel = () => {
    setShowReviews(false);
    setShowDanger(false);
    closeShareSubview();
    setSelectedPostInfo(null);
    setExpandedImage(null);
    setExpandedSource(null);
    onClose?.();
  };

  const handleProfileClick = (username) => {
    if (onOpenProfile) onOpenProfile(username);
  };

const handleUploadImage = async (file) => {
  try {
    

    const token = getActiveAwareAuthToken(window);
    if (!selectedSpot?.id || !file) return;
    if (!token && !viewer?.id) return;


    setUploading(true);

    const fd = new FormData();
    fd.append("file", file);
    if (token) {
      fd.append("token", token);
    }

    const res = await fetch(
      `${API_BASE}/map/spots/${selectedSpot.id}/images`,
      withApiCredentials({
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      })
    );

    if (!res.ok) {
  const txt = await res.text();
  throw new Error(txt || "Upload failed");
}


    const img = await res.json();
    setSpotImages((prev) => {
  const next = [...prev, img];
  setActiveImageIndex(next.length - 1);
  return next;
});

  } catch (e) {
  console.error("Upload failed:", e.message || e);
  alert(
    t("map_upload_failed", {
      message: e.message || t("common_unknown_error"),
    })
  );
}
 finally {
    setUploading(false);
  }
};

const saveFeatureConsent = async (consentType) => {
  const token = getActiveAwareAuthToken(window);
  if (!token && !viewer?.id) {
    throw new Error("missing_token");
  }

  const response = await fetch(
    `${API_BASE}/auth/accept-feature-consent`,
    withApiCredentials({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ consentType }),
    })
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.detail || "save_failed");
  }

  onUserUpdated?.(data);
  return data;
};

const showPrevImage = () => {
  if (expandedSource !== "spot" || !spotImages.length) return;


  setActiveImageIndex((i) => {
    const next = i === 0 ? spotImages.length - 1 : i - 1;

    if (expandedImage) {
      setExpandedImage(`${API_BASE}${spotImages[next].url}`);
    }

    return next;
  });
};

const showNextImage = () => {
  if (expandedSource !== "spot" || !spotImages.length) return;


  setActiveImageIndex((i) => {
    const next = i === spotImages.length - 1 ? 0 : i + 1;

    if (expandedImage) {
      setExpandedImage(`${API_BASE}${spotImages[next].url}`);
    }

    return next;
  });
};

const handleShareWithFriend = async (friendId, options = {}) => {
  try {
    const token = getActiveAwareAuthToken(window);
    if (!selectedSpot) return;
    if (!token && !viewer?.id) return;

    if (!options.skipLocationConsent && viewer?.requiresLocationSharingConsent) {
      setPendingShareFriendId(friendId);
      setLocationConsentError("");
      setShowLocationConsent(true);
      return;
    }

    setSharing(true);

    const fd = new FormData();
    if (token) {
      fd.append("token", token);
    }
    fd.append("to_user_id", friendId);
    fd.append("lat", selectedSpot.lat);
    fd.append("lng", selectedSpot.lng);
    fd.append(
      "location",
      derivedPlaceName ||
        selectedSpot.location ||
        selectedSpot.name ||
        ""
    );

    const res = await fetch(
      `${API_BASE}/map/share`,
      withApiCredentials({
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      })
    );

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || "Share failed");
    }

    alert(t("map_location_shared"));
    closeShareSubview();
  } catch (e) {
    console.error("Share failed:", e);
    alert(t("map_share_failed"));
  } finally {
    setSharing(false);
  }
};

const handleAcceptLocationConsent = async () => {
  setIsSavingLocationConsent(true);
  setLocationConsentError("");

  try {
    await saveFeatureConsent("acceptLocationSharing");
    const friendId = pendingShareFriendId;
    setPendingShareFriendId(null);
    setShowLocationConsent(false);

    if (friendId) {
      await handleShareWithFriend(friendId, { skipLocationConsent: true });
    }
  } catch (e) {
    console.error("Failed to save location consent:", e);
    setLocationConsentError(
      t("map_location_consent_save_failed")
    );
  } finally {
    setIsSavingLocationConsent(false);
  }
};

const handleCancelLocationConsent = () => {
  setPendingShareFriendId(null);
  setLocationConsentError("");
  setShowLocationConsent(false);
};


  return (
    <>
      <div ref={contentRef} className="map-legend-container catch-marker-details">
        <div className={`catch-marker-details__card${showReviews ? " catch-marker-details__card--catches" : ""}${showDanger ? " catch-marker-details__card--dangers" : ""}`}>
        {/* HEADER */}
        <div className="map-legend-header">
          <div className="catch-marker-details__mobile-header">
            <div className="catch-marker-details__heading">
              <span className="catch-marker-details__marker-icon" aria-hidden="true">
                <Fish size={27} strokeWidth={2.25} />
              </span>
              <div className="catch-marker-details__heading-copy">
                <h2 className="catch-marker-details__title">{legendTitle}</h2>
                <p className="catch-marker-details__coordinates">
                  <MapPin size={17} strokeWidth={2.2} aria-hidden="true" />
                  <span>
                    {selectedSpot.lat.toFixed(3)}, {selectedSpot.lng.toFixed(3)}
                  </span>
                </p>
              </div>
            </div>
            <button
              type="button"
              className="catch-marker-details__close"
              onClick={handleClosePanel}
              aria-label={t("map_close_catch_location_details")}
            >
              <X size={23} strokeWidth={2.4} aria-hidden="true" />
            </button>
          </div>

          {!showReviews && (
          <div className="catch-marker-details__actions">
            <button
              type="button"
              className={`catch-marker-details__action${isFavorited ? " is-active" : ""}`}
              disabled={isUpdatingFavorite}
              aria-pressed={isFavorited}
              onClick={handleFavoriteClick}
            >
              <Heart
                size={21}
                strokeWidth={2.2}
                fill={isFavorited ? "currentColor" : "none"}
                aria-hidden="true"
              />
              <span>{isFavorited ? t("favorited") : t("favorite")}</span>
            </button>

            <a
              href={`https://www.google.com/maps?q=${selectedSpot.lat},${selectedSpot.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="catch-marker-details__action"
            >
              <Navigation size={21} strokeWidth={2.2} aria-hidden="true" />
              <span>{t("map_open_in_google_maps")}</span>
            </a>

            <button
              type="button"
              className="catch-marker-details__action"
              aria-expanded={showDanger}
              onClick={() => (showDanger ? showSummary() : handleOpenDangers())}
            >
              <TriangleAlert size={21} strokeWidth={2.2} aria-hidden="true" />
              <span>{t("map_dangers")}</span>
            </button>

            {selectedSpot?.id &&
              selectedSpot.id.includes("_") &&
              selectedSpot.isPrivate !== true && (
                <label
                  className={`catch-marker-details__action catch-marker-details__upload${uploading ? " is-loading" : ""}`}
                >
                  <Camera size={21} strokeWidth={2.2} aria-hidden="true" />
                  <span>
                    {uploading ? t("map_uploading") : t("map_add_location_photo")}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    disabled={uploading}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) handleUploadImage(file);
                    }}
                  />
                </label>
              )}

            <button
              type="button"
              className="catch-marker-details__action catch-marker-details__action--share"
              onClick={handleOpenShare}
            >
              <Share2 size={21} strokeWidth={2.2} aria-hidden="true" />
              <span>{t("map_share_location")}</span>
            </button>
          </div>
          )}

          {/* DANGER PANEL */}
          {showDanger && (
            <div className="danger-panel">
              <DangerInfo
                lat={selectedSpot.lat}
                lon={selectedSpot.lng}
                onClose={showSummary}
              />
            </div>
          )}

          {/* Location images */}
{!showReviews && (
  <div className="spot-images-section">
    <h3>
      <Images
        className="catch-marker-details__section-icon"
        size={21}
        strokeWidth={2.2}
        aria-hidden="true"
      />
      <span>{t("map_location_images")}</span>
    </h3>

    {spotImages.length > 0 && spotImages[activeImageIndex] ? (
      <>
        <div className="carousel">
          {spotImages.length > 1 && (
            <button
              type="button"
              className="carousel-arrow left"
              aria-label={t("map_previous_image")}
              onClick={(e) => {
                e.stopPropagation();
                showPrevImage();
              }}
            >
              {"\u2039"}
            </button>
          )}

          <img
            src={`${API_BASE}${spotImages[activeImageIndex].url}`}
            alt=""
            className="carousel-image"
            onClick={() => {
    setExpandedSource("spot");
    setExpandedImage(
      `${API_BASE}${spotImages[activeImageIndex].url}`
    );
  }}

          />

          {spotImages.length > 1 && (
            <button
              type="button"
              className="carousel-arrow right"
              aria-label={t("map_next_image")}
              onClick={(e) => {
                e.stopPropagation();
                showNextImage();
              }}
            >
              {"\u203A"}
            </button>
          )}

          {spotImages.length > 1 && (
            <div className="catch-marker-details__carousel-dots">
              {spotImages.map((image, index) => (
                <button
                  key={image.id || image.url || index}
                  type="button"
                  className={`catch-marker-details__carousel-dot${index === activeImageIndex ? " is-active" : ""}`}
                  aria-label={t("map_location_image_number", { count: index + 1 })}
                  aria-current={index === activeImageIndex ? "true" : undefined}
                  onClick={(event) => {
                    event.stopPropagation();
                    setActiveImageIndex(index);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {spotImages.length > 1 && (
          <div className="catch-marker-details__desktop-thumbnails">
            {spotImages.map((image, index) => (
              <button
                key={image.id || image.url || index}
                type="button"
                className={`catch-marker-details__thumbnail${index === activeImageIndex ? " is-active" : ""}`}
                aria-label={t("map_location_image_number", { count: index + 1 })}
                aria-current={index === activeImageIndex ? "true" : undefined}
                onClick={() => setActiveImageIndex(index)}
              >
                <img src={`${API_BASE}${image.url}`} alt="" />
              </button>
            ))}
          </div>
        )}
      </>
    ) : (
      <div className="spot-image-placeholder">
        {t("map_no_images_yet")}
      </div>
    )}
  </div>
)}



          {!showReviews && (
            <div className="catch-marker-details__mobile-summary">
              <div className="catch-marker-details__rating">
                <Star
                  className="catch-marker-details__rating-icon"
                  size={22}
                  strokeWidth={2.15}
                  fill="currentColor"
                  aria-hidden="true"
                />
                <strong>{t("map.location_rating")}:</strong>
                {locationRatingStats ? (
                  <>
                    <span className="stars-display">
                      {renderStars(locationRatingStats.avg)}
                    </span>
                    <strong>{locationRatingStats.avg.toFixed(1)}</strong>
                  </>
                ) : (
                  <span className="catch-marker-details__no-rating">
                    {t("map_no_ratings")}
                  </span>
                )}
              </div>

              {catchCount > 0 ? (
                <button
                  type="button"
                  className="catch-marker-details__catches-link"
                  onClick={handleOpenLocationCatches}
                >
                  <Fish size={22} strokeWidth={2.2} aria-hidden="true" />
                  <span>{t("map_catches_from_location", { count: catchCount })}</span>
                  <ChevronRight size={20} strokeWidth={2.3} aria-hidden="true" />
                </button>
              ) : (
                <div className="catch-marker-details__catches-empty">
                  <Fish size={21} strokeWidth={2.1} aria-hidden="true" />
                  <span>{t("map_no_catches_at_location")}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* REVIEW LIST WITH IMAGES */}
        {showReviews && (
          <section className="reviews-list catch-marker-details__catches" aria-labelledby="catch-marker-catches-title">
            <div className="catch-marker-details__catches-header">
              <div>
                <h3 id="catch-marker-catches-title">
                  {t("map_catches_section_title")}
                </h3>
                <span className="catch-marker-details__catches-count">
                  {t("map_catch_count", { count: catchCount })}
                </span>
              </div>
            </div>
            {spotReviews.map((r, i) => {
              const d = r.catch || r;
              const username = typeof r.user === "string"
                ? r.user
                : r.user?.username || r.username || "";

              const rawImage = r.mediaUrl || null;
              const mediaUrl = rawImage
  ? `${API_BASE}${rawImage}`
  : null;


              return (
                <article key={r.id || i} className={`review-item${mediaUrl ? "" : " review-item--no-image"}`}>
                  {/* THUMBNAIL */}
                  {mediaUrl && (
                    <img
                      src={mediaUrl}
                      alt={t("map.catch_image")}
                      onClick={() => {
  setExpandedSource("review");
  setExpandedImage(mediaUrl);
}}

                      className="review-thumbnail"
                    />
                  )}

                  <div className="review-content">
                    {/* USER */}
                    <button
                      type="button"
                      className="review-username"
                      onClick={() => handleProfileClick(username)}
                    >
                      {username}
                    </button>

                    {/* RATING */}
                    {d.rating > 0 && (
                      <div className="review-rating">
                        {"\u2605".repeat(d.rating)}
                      </div>
                    )}

                    {/* TEXT */}
                    {(d.feedback || r.caption) && (
  <div className="review-text">
    {d.feedback || r.caption}
  </div>
)}


                    {/* INFO BUTTON */}
                    <button
                      type="button"
                      onClick={() => setSelectedPostInfo(r)}
                      className="info-button"
                      aria-label={t("map_view_catch_information", { username })}
                    >
                      <Info size={20} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                  </div>
                </article>
              );
            })}
          </section>
        )}

        {/* FULLSCREEN IMAGE MODAL */}
        {expandedImage && (
  <div
    onClick={() => {
      setExpandedImage(null);
      setExpandedSource(null);
    }}
    className="fullscreen-modal"
  >
    {/* Close button */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        setExpandedImage(null);
        setExpandedSource(null);
      }}
      className="fullscreen-close"
    >
      {"\u00D7"}
    </button>

    {/* Left arrow */}
    {expandedSource === "spot" && spotImages.length > 1 && (

      <button
        className="carousel-arrow left fullscreen-arrow"
        onClick={(e) => {
          e.stopPropagation();
          showPrevImage();
        }}
      >
        {"\u2039"}
      </button>
    )}

    {/* Image */}
    <img
      src={expandedImage}
      alt={t("map_expanded_image_alt")}
      className="fullscreen-image"
    />

    {/* Right arrow */}
    {spotImages.length > 1 && (
      <button
        className="carousel-arrow right fullscreen-arrow"
        onClick={(e) => {
          e.stopPropagation();
          showNextImage();
        }}
      >
        {"\u203A"}
      </button>
    )}
  </div>
)}

{showShareModal && (
  <div className="share-modal-overlay" onClick={closeShareSubview}>
    <div
      className="share-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="catch-marker-share-title"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="share-modal__header">
        <span className="share-modal__icon" aria-hidden="true">
          <Share2 size={23} strokeWidth={2.2} />
        </span>
        <h3 id="catch-marker-share-title">{t("map_share_location")}</h3>
        <button
          type="button"
          className="share-modal__close"
          onClick={closeShareSubview}
          aria-label={t("map_close_share_location")}
        >
          <X size={21} strokeWidth={2.3} aria-hidden="true" />
        </button>
      </div>

      <label className="share-search-field">
        <Search size={20} strokeWidth={2.2} aria-hidden="true" />
        <input
          type="search"
          placeholder={t("map_search_anglers")}
          value={friendQuery}
          onChange={(e) => setFriendQuery(e.target.value)}
          className="share-search"
        />
      </label>

      <div className="share-friends-list">
        {friendsLoading && (
          <p className="share-modal__status">{t("map_share_loading_anglers")}</p>
        )}
        {!friendsLoading && friendsError && (
          <p className="share-modal__status" role="alert">{t("map_share_anglers_error")}</p>
        )}
        {!friendsLoading && !friendsError && friends
          .filter((u) => u.username?.toLowerCase().includes(friendQuery.trim().toLowerCase()))
          .map((u) => (
    <button
      type="button"
      key={u.id}
      disabled={sharing}
      className="share-friend-item"
      onClick={() => handleShareWithFriend(u.id)}
    >
      <img
        src={
          u.avatar
            ? `${API_BASE}${u.avatar}`
            : "https://cdn-icons-png.flaticon.com/512/847/847969.png"
        }
        alt=""
        className="share-friend-avatar"
      />
      <span className="share-friend-item__name">{u.username}</span>
      <ChevronRight size={20} strokeWidth={2.2} aria-hidden="true" />
    </button>
  ))}
        {!friendsLoading && !friendsError && friends.filter((u) =>
          u.username?.toLowerCase().includes(friendQuery.trim().toLowerCase())
        ).length === 0 && (
          <p className="share-modal__status">{t("map_no_anglers_found")}</p>
        )}

      </div>

      <button
        type="button"
        className="close-button share-modal__footer-close"
        onClick={closeShareSubview}
      >
        {t("common_close")}
      </button>
    </div>
  </div>
)}

<FeatureConsentModal
  consentKey="acceptLocationSharing"
  title={t("map_location_consent_title")}
  isOpen={showLocationConsent}
  isSubmitting={isSavingLocationConsent}
  error={locationConsentError}
  onAccept={handleAcceptLocationConsent}
  onCancel={handleCancelLocationConsent}
/>

        <div className="legend-footer">
  {showReviews && (
    <>
      <AppBackButton
        className="back-button"
        label={t("map_back_to_location")}
        onClick={showSummary}
      />
      {catchCount > 1 && (
        <button type="button" onClick={handleClosePanel} className="close-button catch-marker-details__bottom-close">
          {t("common_close")}
        </button>
      )}
    </>
  )}

  <button type="button" onClick={handleClosePanel} className="close-button">
    {t("common_close")}
  </button>
</div>

      </div>
      </div>

      {/* POPUP */}
      {selectedPostInfo && (
        <InfoPopup
          post={selectedPostInfo}
          posts={posts}
          onClose={() => setSelectedPostInfo(null)}
          hideRating={true}
          hideLocationButton={true}
          enableFishipediaFishLink={true}
          onNavigateAway={onPostInfoNavigateAway}
        />
      )}
    </>
  );
}
